import React, { useState, useEffect, useRef } from 'react';
import { Code, Shield, Cpu, Network, Globe } from 'lucide-react';
import anime from 'animejs';
import { commandMap } from './commands';
import Banner from './Banner';
import TypedOutput from './TypedOutput';
import animationManager from './utils/AnimationManager';
import ParticleBackground from './components/ParticleBackground';
import NavigationDock from './components/NavigationDock';
import HeroSection from './components/HeroSection';
import ProjectCards from './components/ProjectCards';
import ScanningLine from './components/ScanningLine';
import MatrixRain from './components/MatrixRain';
import './App.css';

const CyberTerminal = () => {
  const [commandHistory, setCommandHistory] = useState([
    { type: 'banner', content: <Banner /> },
    { type: 'output', content: '> [Initializing secure environment...]' },
    { type: 'output', content: '> [Connection established]' }
  ]);
  const [input, setInput] = useState('');
  const [cursorVisible, setCursorVisible] = useState(true);
  const [showMatrix, setShowMatrix] = useState(false);
  const [showProjects, setShowProjects] = useState(false);
  const terminalRef = useRef(null);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Enhanced command handler
  const handleCommand = (command) => {
    const lowerCommand = command.toLowerCase().trim();
    let response = [];

    if (lowerCommand === 'clear') {
      setCommandHistory([
        { type: 'banner', content: <Banner /> },
        { type: 'output', content: '> Terminal cleared. Systems reset.' }
      ]);
      setShowProjects(false);
      return;
    }

    if (lowerCommand === 'matrix') {
      setShowMatrix(!showMatrix);
      response = [showMatrix ? 'Matrix mode disabled.' : 'Matrix mode activated. Reality bending...'];
    } else if (lowerCommand === 'projects') {
      setShowProjects(!showProjects);
      response = ['Loading project showcase...'];
    } else {
      response = commandMap[lowerCommand] ||
        [`Unknown command: ${command}. Type 'help' for available commands.`];
    }

    const newHistory = [
      ...commandHistory,
      { type: 'input', content: `> ${command}` },
      ...response.map(line => ({ type: 'output', content: line })),
      { type: 'output', content: '' }
    ];
    setCommandHistory(newHistory);

    // Auto-scroll to bottom with animation
    setTimeout(() => {
      if (terminalRef.current && animationManager.shouldAnimate()) {
        anime({
          targets: terminalRef.current,
          scrollTop: terminalRef.current.scrollHeight,
          duration: 500,
          easing: 'easeOutCubic'
        });
      } else if (terminalRef.current) {
        terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
      }
    }, 50);
  };

  // Cursor blinking effect
  useEffect(() => {
    const cursorInterval = setInterval(() => 
      setCursorVisible(prev => !prev), 500);
    return () => clearInterval(cursorInterval);
  }, []);

  // Boot sequence animation
  useEffect(() => {
    if (!containerRef.current || !animationManager.shouldAnimate()) return;

    const timeline = animationManager.createTimeline('boot-sequence');
    
    // Container entrance
    timeline.add({
      targets: containerRef.current,
      opacity: [0, 1],
      scale: [0.9, 1],
      duration: 800,
      easing: 'easeOutElastic(1, .6)'
    });

    // Terminal glow pulse
    animationManager.createAnimation('terminal-glow', {
      targets: containerRef.current,
      boxShadow: [
        '0 0 20px rgba(0, 255, 65, 0.2)',
        '0 0 40px rgba(0, 255, 65, 0.4)',
        '0 0 20px rgba(0, 255, 65, 0.2)'
      ],
      duration: 2000,
      loop: true,
      easing: 'easeInOutQuad'
    });

    return () => {
      animationManager.removeTimeline('boot-sequence');
      animationManager.removeAnimation('terminal-glow');
    };
  }, []);

  // Command input ripple effect
  const handleInputSubmit = (e) => {
    e.preventDefault();
    if (input.trim()) {
      // Ripple animation on submit
      if (inputRef.current && animationManager.shouldAnimate()) {
        const rect = inputRef.current.getBoundingClientRect();
        animationManager.ripple(rect.width / 2, rect.height / 2, inputRef.current.parentElement);
        
        // Flash effect
        anime({
          targets: inputRef.current.parentElement,
          backgroundColor: ['rgba(0, 255, 65, 0.1)', 'rgba(4, 4, 16, 1)'],
          duration: 300,
          easing: 'easeOutQuad'
        });
      }
      
      handleCommand(input);
      setInput('');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1a1a3a] to-[#0a0a2a] flex flex-col items-center justify-center p-2 sm:p-4 overflow-hidden">
      {/* Background Effects */}
      <ParticleBackground particleCount={window.innerWidth < 768 ? 30 : 60} />
      <ScanningLine />
      <MatrixRain active={showMatrix} opacity={0.2} />

      {/* Hero Section */}
      <HeroSection />

      {/* Main Terminal Container */}
      <div
        ref={containerRef}
        className="w-full max-w-4xl bg-gradient-to-br from-[#141432] to-[#0e0e22] rounded-lg sm:rounded-xl shadow-2xl border border-[#00ff41]/20 sm:border-2 overflow-hidden relative z-10 opacity-0"
        style={{ fontFamily: "'Fira Code', monospace" }}
      >
        {/* Header */}
        <div className="bg-[#00ff41]/10 p-2 sm:p-4 flex items-center justify-between backdrop-blur-sm">
          <div className="flex items-center space-x-1 sm:space-x-2">
            <Shield className="text-[#00ff41] animate-pulse w-4 h-4 sm:w-6 sm:h-6" />
            <h2 className="text-sm sm:text-xl font-bold text-[#00ff41]" style={{ fontFamily: "'Orbitron', sans-serif" }}>
              <span className="hidden sm:inline">DeezSec's CyberTerminal</span>
              <span className="sm:hidden">DeezSec</span>
            </h2>
          </div>
          <div className="flex items-center gap-1 sm:gap-3">
            <div className="text-xs text-[#00ff41]/70 hidden md:block">
              ACCESS LEVEL: TS/SCI
            </div>
            <button
              onClick={() => setShowMatrix(!showMatrix)}
              className="px-2 sm:px-3 py-1 text-[10px] sm:text-xs bg-[#00ff41]/20 border border-[#00ff41]/40 rounded hover:bg-[#00ff41]/30 transition-all text-[#00ff41]"
              title="Toggle Matrix Mode"
            >
              MATRIX
            </button>
          </div>
        </div>

        {/* Project Showcase (toggleable) */}
        {showProjects && (
          <div className="p-2 sm:p-4 md:p-6 border-b border-[#00ff41]/20">
            <ProjectCards />
          </div>
        )}

        {/* Terminal Output */}
        <div 
          ref={terminalRef}
          className="h-[300px] sm:h-[350px] md:h-[400px] overflow-y-auto p-2 sm:p-4 font-mono text-xs sm:text-sm bg-[#0a0a1a] text-[#00ff41] custom-scrollbar"
        >
          {commandHistory.map((item, idx) => {
            switch (item.type) {
              case 'banner':
                return <div key={idx} className="command-line">{item.content}</div>;
              case 'input':
                return (
                  <div key={idx} className="command-line command-input font-bold">
                    {item.content}
                  </div>
                );
              case 'output':
                return <TypedOutput key={idx} text={item.content} />;
              default:
                return null;
            }
          })}
        </div>

        {/* Command Input */}
        <form 
          onSubmit={handleInputSubmit}
          className="flex items-center p-2 sm:p-4 bg-[#040410] relative overflow-hidden"
        >
          <span className="text-[#00ff41] mr-2 font-bold">λ</span>
          <input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            className="flex-grow bg-transparent text-[#00ff41] outline-none placeholder-[#00ff41]/50 text-xs sm:text-base"
            placeholder="Type 'help' for commands"
            autoFocus
          />
          <span 
            className={`ml-2 ${cursorVisible ? 'visible' : 'invisible'} text-[#00ff41]`}
          >
            ▮
          </span>
        </form>
      </div>

      {/* Navigation Dock */}
      <NavigationDock onCommandSelect={handleCommand} />

      {/* Footer */}
      <footer className="mt-4 sm:mt-6 text-center text-[#00ff41]/70 relative z-10 mb-20 sm:mb-4">
        <div className="flex justify-center space-x-3 sm:space-x-4 mb-2">
          <Code className="hover:text-[#00ff41] transition-colors cursor-pointer hover:scale-110 transform w-4 h-4 sm:w-6 sm:h-6" />
          <Network className="hover:text-[#00ff41] transition-colors cursor-pointer hover:scale-110 transform w-4 h-4 sm:w-6 sm:h-6" />
          <Cpu className="hover:text-[#00ff41] transition-colors cursor-pointer hover:scale-110 transform w-4 h-4 sm:w-6 sm:h-6" />
          <Globe className="hover:text-[#00ff41] transition-colors cursor-pointer hover:scale-110 transform w-4 h-4 sm:w-6 sm:h-6" />
        </div>
        <p className="text-[10px] sm:text-xs px-2" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
          © 2024 DeezSec | Enhanced with Anime.js • Powered by React
        </p>
        <p className="text-[10px] sm:text-xs mt-1 opacity-50 px-2">
          <span className="hidden sm:inline">Try commands: help • projects • matrix • whoami</span>
          <span className="sm:hidden">Type 'help' for commands</span>
        </p>
      </footer>
    </div>
  );
};

export default CyberTerminal;
